import React, { useState, useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import * as Icons from "lucide-react"
import { emailApi } from "../services/emailApi"

const JOB_PAGE_SIZE = 10

// Only offer resume actions on document-type attachments.
const RESUME_EXTS = [".pdf", ".doc", ".docx", ".rtf", ".odt", ".txt"]
export function isResumeAttachment(att) {
  const name = (att?.filename || "").toLowerCase()
  const ctype = (att?.content_type || "").toLowerCase()
  if (RESUME_EXTS.some(ext => name.endsWith(ext))) return true
  return (
    ctype.includes("pdf") ||
    ctype.includes("word") ||
    ctype.includes("officedocument") ||
    ctype.includes("msword")
  )
}

function getUserInfo() {
  try {
    return JSON.parse(sessionStorage.getItem("user-info") || "{}")
  } catch {
    return {}
  }
}
function getCompanies() {
  const info = getUserInfo()
  const list = info?.user_details?.companies_data || []
  const mapped = list
    .filter(c => c && c.company_id != null)
    .map(c => ({
      id: String(c.company_id),
      name: c.company_name || `Company ${c.company_id}`,
    }))
  if (mapped.length) return mapped
  // Fallback to a single company id if companies_data isn't present.
  const cid = info.company_id || info.company
  return cid ? [{ id: String(cid), name: `Company ${cid}` }] : []
}

export default function AttachmentActions({ attachment, showToast }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, openUp: false })
  const [modalOpen, setModalOpen] = useState(false)
  const [mode, setMode] = useState("job") // 'job' | 'database'
  const [busy, setBusy] = useState(false)

  const companies = getCompanies()
  const [companyId, setCompanyId] = useState(companies[0]?.id || "")

  // Job picker state
  const [jobs, setJobs] = useState([])
  const [jobSearch, setJobSearch] = useState("")
  const [jobPage, setJobPage] = useState(1)
  const [jobHasNext, setJobHasNext] = useState(false)
  const [jobHasPrev, setJobHasPrev] = useState(false)
  const [jobsLoading, setJobsLoading] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState("")

  const btnRef = useRef(null)

  useEffect(() => {
    if (!menuOpen) return undefined
    const close = () => setMenuOpen(false)
    document.addEventListener("mousedown", close)
    window.addEventListener("scroll", close, true)
    return () => {
      document.removeEventListener("mousedown", close)
      window.removeEventListener("scroll", close, true)
    }
  }, [menuOpen])

  const openMenu = e => {
    e.stopPropagation()
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const openUp = window.innerHeight - r.bottom < 130 // not enough room below
      setMenuPos({
        left: Math.max(8, r.right - 220),
        top: openUp ? r.top - 8 : r.bottom + 4,
        openUp,
      })
    }
    setMenuOpen(o => !o)
  }

  const fetchJobs = useCallback(
    async (page = 1, search = "", company = companyId) => {
      setJobsLoading(true)
      try {
        const params = { page, size: JOB_PAGE_SIZE }
        if (company) params.company_id = company
        if (search) params.search = search
        const res = await emailApi.getJobOrders(params)
        const list =
          res.data?.results ||
          res.data?.data?.results ||
          (Array.isArray(res.data) ? res.data : [])
        const count = res.data?.count ?? res.data?.data?.count ?? null
        const nextUrl = res.data?.next ?? res.data?.data?.next ?? null
        const prevUrl = res.data?.previous ?? res.data?.data?.previous ?? null
        setJobs(list)
        setJobPage(page)
        setJobHasNext(
          nextUrl != null || (count != null && page * JOB_PAGE_SIZE < count)
        )
        setJobHasPrev(prevUrl != null || page > 1)
      } catch (e) {
        showToast?.("Failed to load job orders.", "error")
      } finally {
        setJobsLoading(false)
      }
    },
    [companyId, showToast]
  )

  const openModal = m => {
    setMenuOpen(false)
    setMode(m)
    setSelectedJobId("")
    setJobSearch("")
    setModalOpen(true)
    if (m === "job") fetchJobs(1, "", companyId)
  }

  const onCompanyChange = e => {
    const cid = e.target.value
    setCompanyId(cid)
    setSelectedJobId("")
    if (mode === "job") fetchJobs(1, jobSearch.trim(), cid)
  }

  const onSearchSubmit = e => {
    e.preventDefault()
    fetchJobs(1, jobSearch.trim(), companyId)
  }

  const confirmAdd = async () => {
    if (!companyId) {
      showToast?.("Select a company first.", "error")
      return
    }
    if (mode === "job" && !selectedJobId) {
      showToast?.("Select a job order first.", "error")
      return
    }
    setBusy(true)
    try {
      const attId = attachment.attachment_id || attachment.id
      const res =
        mode === "job"
          ? await emailApi.attachmentToJob({
              attachment_id: attId,
              job_id: selectedJobId,
              company_id: companyId,
            })
          : await emailApi.attachmentToDatabase({
              attachment_id: attId,
              company_id: companyId,
            })

      const data = res?.data || {}
      const defaultMsg =
        mode === "job"
          ? "Resume added to the job order."
          : "Resume added to the candidates database."

      if (data.status === "skipped") {
        // Nothing was created (e.g. candidate already exists / already mapped) —
        // tell the user exactly what happened instead of a false "success".
        showToast?.(
          data.message ||
            "Nothing was added — this candidate is already in the system.",
          "warning"
        )
        // Keep the dialog open for the job case so they can pick a different job.
        if (mode !== "job") setModalOpen(false)
      } else {
        showToast?.(data.message || defaultMsg, "success")
        setModalOpen(false)
      }
    } catch (err) {
      showToast?.(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not add the resume.",
        "error"
      )
    } finally {
      setBusy(false)
    }
  }

  const menuStyle = {
    position: "fixed",
    left: menuPos.left,
    ...(menuPos.openUp
      ? { top: "auto", bottom: window.innerHeight - menuPos.top }
      : { top: menuPos.top }),
  }

  return (
    <div className="em-att-actions">
      <button
        ref={btnRef}
        type="button"
        className="em-att-action-btn"
        onClick={openMenu}
        disabled={busy}
        title="Add resume to…"
      >
        {busy ? (
          <Icons.Loader2 size={13} className="em-spin" />
        ) : (
          <Icons.Plus size={13} />
        )}
        <span>Add</span>
        <Icons.ChevronDown size={12} />
      </button>

      {menuOpen && (
        <div
          className="em-att-menu"
          style={menuStyle}
          onMouseDown={e => e.stopPropagation()}
        >
          <button
            type="button"
            className="em-att-menu-item"
            onClick={() => openModal("job")}
          >
            <Icons.Briefcase size={14} /> Add to Job order
          </button>
          <button
            type="button"
            className="em-att-menu-item"
            onClick={() => openModal("database")}
          >
            <Icons.Database size={14} /> Add to Candidates database
          </button>
        </div>
      )}

      {modalOpen && createPortal(
        <div
          className="em-jobpick-overlay"
          onClick={() => !busy && setModalOpen(false)}
        >
          <div className="em-jobpick" onClick={e => e.stopPropagation()}>
            <div className="em-jobpick-head">
              <div className="em-jobpick-title">
                <span className="em-jobpick-title-main">
                  {mode === "job" ? "Add to job order" : "Add to candidates database"}
                </span>
                <span
                  className="em-jobpick-title-file"
                  title={attachment.filename}
                >
                  {attachment.filename}
                </span>
              </div>
              <button
                type="button"
                className="em-jobpick-x"
                onClick={() => setModalOpen(false)}
              >
                <Icons.X size={16} />
              </button>
            </div>

            <div className="em-jobpick-company">
              <label>Company</label>
              <select value={companyId} onChange={onCompanyChange}>
                {companies.length === 0 && (
                  <option value="">No company available</option>
                )}
                {companies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {mode === "job" && (
              <>
                <form className="em-jobpick-search" onSubmit={onSearchSubmit}>
                  <Icons.Search size={14} />
                  <input
                    type="text"
                    placeholder="Search job orders…"
                    value={jobSearch}
                    onChange={e => setJobSearch(e.target.value)}
                  />
                  <button type="submit" className="em-jobpick-searchbtn">
                    Search
                  </button>
                </form>

                <div className="em-jobpick-list">
                  {jobsLoading ? (
                    <div className="em-jobpick-empty">Loading…</div>
                  ) : jobs.length === 0 ? (
                    <div className="em-jobpick-empty">
                      No job orders found for this company.
                    </div>
                  ) : (
                    jobs.map(j => {
                      const id = String(j.id || j.job_id || "")
                      const title = j.job_title || j.position || `Job #${id}`
                      const joid = j.job_order_id || j.job_number || id
                      return (
                        <label
                          key={id}
                          className={`em-jobpick-item ${
                            selectedJobId === id ? "em-jobpick-item-sel" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="jobpick"
                            checked={selectedJobId === id}
                            onChange={() => setSelectedJobId(id)}
                          />
                          <span className="em-jobpick-item-txt">
                            {joid} — {title}
                          </span>
                        </label>
                      )
                    })
                  )}
                </div>

                <div className="em-jobpick-pager">
                  <button
                    type="button"
                    onClick={() =>
                      jobHasPrev &&
                      !jobsLoading &&
                      fetchJobs(jobPage - 1, jobSearch.trim(), companyId)
                    }
                    disabled={!jobHasPrev || jobsLoading}
                  >
                    <Icons.ChevronLeft size={14} /> Prev
                  </button>
                  <span>Page {jobPage}</span>
                  <button
                    type="button"
                    onClick={() =>
                      jobHasNext &&
                      !jobsLoading &&
                      fetchJobs(jobPage + 1, jobSearch.trim(), companyId)
                    }
                    disabled={!jobHasNext || jobsLoading}
                  >
                    Next <Icons.ChevronRight size={14} />
                  </button>
                </div>
              </>
            )}

            {mode === "database" && (
              <div className="em-jobpick-dbnote">
                The resume will be parsed and added to the selected company’s
                candidates database.
              </div>
            )}

            <div className="em-jobpick-foot">
              <button
                type="button"
                className="em-jobpick-cancel"
                onClick={() => setModalOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="em-jobpick-add"
                onClick={confirmAdd}
                disabled={
                  busy || !companyId || (mode === "job" && !selectedJobId)
                }
              >
                {busy
                  ? "Adding…"
                  : mode === "job"
                  ? "Add to job"
                  : "Add to database"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
